import { ExternalTokenizer, LRParser } from '@lezer/lr';
import { LRLanguage, LanguageSupport } from '@codemirror/language';
import { styleTags, tags } from '@lezer/highlight';

// This file was generated by lezer-generator. You probably shouldn't edit it.
const Comment = 1,
  MultilineComment = 2,
  Slash = 3,
  Backslash = 4,
  MultilineCommentToEOF = 5,
  Minus = 6,
  Number = 7;

/* Hand-written tokenizers for K tokens that can't be
   expressed by lezer's built-in tokenizer. */

const A = 65, a = 97, backslash = 92, closeBrackets = 125, closeParenthesis = 41,
    closeSquareBrackets = 93, e = 101, eof = -1, minus = 45, N = 78, n = 110, newline = 10, nine = 57, period = 46,
    slash = 47, space = 32, x = 120, Z = 90, z = 122, zero = 48;

// This tokenizer is needed to determine whether a `/` is used as an adverb or is
// the beginning of a comment. This requires looking at the previous character and
// the position in the input. This tokenizer is also used to parse multiline comments.
const slashOrComment = new ExternalTokenizer(input => {
    if (input.next !== slash) {
        return;
    }

    const prev = input.peek(-1);
    const pos = input.pos;
    input.advance();

    if (prev !== space && prev !== newline && pos !== 0) {
        input.acceptToken(Slash);
        return;
    }

    if (input.next === newline && (prev === newline || pos === 0)) {
        // It's a multiline comment.
        while (input.next !== eof) {
            if (input.next === backslash && input.peek(-1) === newline) {
                input.advance();
                if (input.next === newline) {
                    break;
                }
            }
            input.advance();
        }
        input.acceptToken(MultilineComment);
        return;
    }

    while (input.next !== newline && input.next !== eof) {
        input.advance();
    }
    input.acceptToken(Comment);
});

// This tokenizer is needed to determine whether a `\` is used as an adverb or is
// the beginning of a comment to the end of the file. This requires looking at the previous
// character and the position in the input.
const backslashOrMultilineCommentToEOF = new ExternalTokenizer(input => {
    if (input.next !== backslash) {
        return;
    }

    const prev = input.peek(-1);
    const pos = input.pos;
    input.advance();

    if (input.next === backslash && (pos === 0 || prev === newline)) {
        input.advance();

        if (input.next === newline || input.next === eof) {
            input.advance();
            while (input.next !== eof) {
                input.advance();
            }
            input.acceptToken(MultilineCommentToEOF);
            return;
        }
        return;
    }

    input.acceptToken(Backslash);
});

// This tokenizer is needed to determine whether a `-` is used as an verb or
// the beginning of a number. This requires looking at the previous character.
// This tokenizer also includes full support for parsing floating point numbers
// and is a convenient way to avoid some overlapping tokens, such as `.e`, that
// would occur when using the built-in tokenizer.
const minusOrNumber = new ExternalTokenizer(input => {
    // Don't allow a trailing `x`, since that overlaps with hexadecimal constants like `0x100`.
    if (input.next >= zero && input.next <= nine && input.peek(1) !== x) {
        parseNumber(input);
        input.acceptToken(Number);
        return;
    }

    if (input.next !== minus) {
        return;
    }

    const prev = input.peek(-1);

    input.advance();

    if ((prev < zero || prev > nine) && (prev < A || prev > Z) && (prev < a || prev > z) &&
        prev !== closeBrackets && prev !== closeParenthesis && prev !== closeSquareBrackets) {
        if (parseNumber(input)) {
            input.acceptToken(Number);
            return;
        }
    }

    input.acceptToken(Minus);
});

const parseNumber = input => {
    let isNumber;
    let isSingleZero;
    while (input.next >= zero && input.next <= nine) {
        isSingleZero = !isNumber && input.next === zero;
        isNumber = true;
        input.advance();
    }

    if (isSingleZero && (input.next === n || input.next === N)) {
        // Support `0N` and `0n`.
        input.advance();
        return true;
    }

    if (isNumber) {
        if (input.next === period) {
            input.advance();
        }

        while (input.next >= zero && input.next <= nine) {
            input.advance();
        }

        if (input.next === e) {
            input.advance();

            if (input.next === minus) {
                input.advance();
            }

            while (input.next >= zero && input.next <= nine) {
                input.advance();
            }
        }
    }
    return isNumber;
};

// This file was generated by lezer-generator. You probably shouldn't edit it.
const parser = LRParser.deserialize({
  version: 14,
  states: "#rQVQ_OOOOQ^'#Cy'#CyOOQ^'#Cz'#CzOOQ^'#C{'#C{O!WQ_O'#CpO!_Q_O'#CsOOQ^'#Cx'#CxO!fQ_O'#CtQVQ_OOOOQ^,59[,59[O#vQ_O,59[OOQ^,59_,59_O#}Q_O,59_O$UQ_O'#CmOOQ^,59d,59dOOQ^-E6r-E6rOOQ^1G.v1G.vOOQ^1G.y1G.yOOQ^,59X,59XO$]Q_O,59XOOQ^1G.s1G.s",
  stateData: "$d~OkOS~OPUOQUORROSROTUOUQOVPOXPOYPOZPO[QO]RO^UOcSOfTO~ObXO~PVOeZO~PVO`]OPhXQhXRhXShXThXUhXVhXXhXYhXZhX[hX]hX^hXchXfhXihXbhXehX_hX~Ob`O~PVOeaO~PVO_bO~PVO_dO~PVO",
  goto: "!lpPPPPPPPPPPPPPPPPPqPPtPPt!OPPP!btttR^VaUOSTWY[]cQWOQYSQ[TW_WY[cRc]aVOSTWY[]c",
  nodeNames: "⚠ Comment MultilineComment Slash Backslash MultilineCommentToEOF Minus Number Program Name String Symbol Verb Adverb Delimiter ] [ SquareBrackets ) ( Parentheses } { Function",
  maxTerm: 31,
  nodeProps: [
    ["openedBy", 15,"[",18,"(",21,"{"],
    ["closedBy", 16,"]",19,")",22,"}"]
  ],
  skippedNodes: [0],
  repeatNodeCount: 1,
  tokenData: ")t~RuXY#fYZ#f]^#fpq#fqr#wrs$Ust#wtu#wuv#wvw#wwx$sxy%Qyz%Vz{#w{|#w|}#w!O!P#w!Q!R%[!R![%y![!]#w!]!^&P!^!_#w!_!`#w!`!a#w!a!b#w!b!c#w!c!}&U!}#O&p#P#Q&u#Q#R#w#R#S#w#S#T&z#T#o&U#o#p)j#p#q#w#q#r)o#r#s#w~#kSk~XY#fYZ#f]^#fpq#f~#|P[~![!]$P~$UO[~~$XTOr$Urs$hs#O$U#O#P$m#P~$U~$mOY~~$pPO~$U~$xP]~![!]${~%QO]~~%VOc~~%[Ob~~%_Q![!]#w#l#m%e~%jQY~!Q![%p#T#Z%p~%sQ!Q![%e#T#Z%e~%|P![!]#w~&UO^~~&ZSX~!O!P&g!Q![&U!c!}&U#T#o&U~&jQ!c!}&U#T#o&U~&uO`~~&zO_~~'PWZ~rs'i!Q!R(W!c!}(r#T#o(r#t;'S'{;'S;=_)^;=_;=`)d<%l~'{~'lTOr'irs'{s#O'i#O#P(Q#P~'i~(QOZ~~(TPO~'i~(ZP#l#m(^~(cQZ~!Q![(i#T#Z(i~(lQ!Q![(^#T#Z(^~(wSZ~!O!P)T!Q![(r!c!}(r#T#o(r~)WQ!c!}(r#T#o(r~)aP;=`<%l'{~)gP;=`<%k'{~)oOf~~)tOe~",
  tokenizers: [0, slashOrComment, backslashOrMultilineCommentToEOF, minusOrNumber],
  topRules: {"Program":[0,8]},
  tokenPrec: 0
});

const kLanguage = LRLanguage.define({
    parser: parser.configure({
        props: [
            styleTags({
                "( )": tags.paren,
                "[ ]": tags.squareBracket,
                "{ }": tags.bracket,
                Adverb: tags.operator,
                Backslash: tags.operator,
                Comment: tags.lineComment,
                Delimiter: tags.separator,
                Minus: tags.operator,
                MultilineComment: tags.blockComment,
                MultilineCommentToEOF: tags.blockComment,
                Name: tags.name,
                Number: tags.number,
                Slash: tags.operator,
                String: tags.string,
                Symbol: tags.constant(tags.name),
                Verb: tags.operator,
            })
        ]
    }),
    languageData: {
        commentTokens: { line: "/" },
        closeBrackets: { brackets: ["(", "[", "{", '"'] },
    }
});
function k() {
    return new LanguageSupport(kLanguage);
}

export { k, kLanguage };
